/**
 * Rules for deciding when an in-app route transition has started.
 *
 * The App Router exposes no navigation-start event, so the global progress bar
 * (`~/components/route-progress-bar`) infers it from the click that is about to
 * be handed to `next/link`. The listener runs in the CAPTURE phase — Link calls
 * `preventDefault()` in its own handler, so by the bubble phase a real
 * navigation is indistinguishable from a cancelled one. Every exclusion below
 * therefore has to be explicit.
 *
 * Kept free of React and DOM globals so it can be exercised directly.
 */

/** Opt out on a specific link: `<Link data-no-route-progress ...>`. */
export const NO_ROUTE_PROGRESS_ATTRIBUTE = 'data-no-route-progress';

/**
 * The identity of a route for progress purposes: path plus query, hash ignored.
 *
 * The bar completes when the App Router commits a new path or query, so this is
 * exactly the granularity at which a navigation is observable. Two history
 * entries with the same key differ only by hash — the browser scrolls, the
 * route never re-renders, and a bar started for one would hang until the safety
 * timeout. `isInAppNavigation` filters those out for clicks; `popstate` needs
 * the same guard, and gets it by comparing keys.
 *
 * Accepts `search` with or without its leading `?` — `useSearchParams()`
 * omits it, `location.search` includes it.
 */
export function getRouteKey(pathname: string, search: string) {
  const query = search.startsWith('?') ? search.slice(1) : search;

  return query ? `${pathname}?${query}` : pathname;
}

type ClickIntent = Pick<
  MouseEvent,
  'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'
>;

type AnchorIntent = Pick<HTMLAnchorElement, 'href' | 'target'> & {
  getAttribute(name: string): string | null;
  hasAttribute(name: string): boolean;
};

/**
 * True when this click opens a URL in the current tab rather than a new one,
 * a download, or nothing at all.
 */
export function isPlainNavigationClick(event: ClickIntent) {
  if (event.defaultPrevented) {
    return false;
  }

  // Left button only; middle/right click never navigates this tab.
  if (event.button !== 0) {
    return false;
  }

  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * True when following `anchor` replaces the current document via the router.
 *
 * `currentUrl` is the URL the browser is on right now — a link pointing at it
 * produces no transition, and a link differing only in hash is handled by the
 * browser without the router.
 */
export function isInAppNavigation(anchor: AnchorIntent, currentUrl: string) {
  if (anchor.hasAttribute('download')) {
    return false;
  }

  if (anchor.hasAttribute(NO_ROUTE_PROGRESS_ATTRIBUTE)) {
    return false;
  }

  // `target` other than the current browsing context leaves this page intact.
  if (anchor.target && anchor.target !== '_self') {
    return false;
  }

  const rel = anchor.getAttribute('rel') ?? '';

  if (rel.split(/\s+/).includes('external')) {
    return false;
  }

  let destination: URL;
  let current: URL;

  try {
    // `anchor.href` is already resolved against the document by the DOM. An
    // anchor with no href at all (a menu trigger, a tab header) reports the
    // empty string, which does not parse — so it lands here as "not a
    // navigation" without needing its own branch.
    destination = new URL(anchor.href);
    current = new URL(currentUrl);
  } catch {
    return false;
  }

  // Covers cross-origin links AND every non-http scheme in one comparison:
  // `mailto:`, `tel:`, and friends parse to the opaque origin "null", which
  // never matches the page's own.
  if (destination.origin !== current.origin) {
    return false;
  }

  // Same path and query: either a no-op or a pure in-page hash jump. Neither
  // fetches a new route, so a progress bar would hang until the safety timeout.
  return (
    destination.pathname !== current.pathname ||
    destination.search !== current.search
  );
}

/**
 * The single predicate the progress bar's click listener calls.
 *
 * `resolveAnchor` is injected so this stays independent of how the caller walks
 * up from `event.target` (the DOM's `closest`, or a test's own lookup).
 */
export function shouldStartRouteProgress(
  event: ClickIntent,
  anchor: AnchorIntent | null,
  currentUrl: string,
) {
  if (!anchor) {
    return false;
  }

  return isPlainNavigationClick(event) && isInAppNavigation(anchor, currentUrl);
}
